import { useCallback, useMemo, useSyncExternalStore } from "react"
import { hashKey, useQueryClient } from "@tanstack/react-query"

/**
 * Reads a value another part of the app writes with `setQueryData` and
 * re-renders when it changes. `getQueryData` is a one-off read, so a component
 * that mounts before the writer runs would otherwise never see the value.
 *
 * The cache subscription is scoped to this key's hash so unrelated updates
 * (transactions, accounts, …) do not wake this store.
 */
export function useCachedQueryData<T>(key: string): T | undefined {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => [key] as const, [key])
  const queryHash = useMemo(() => hashKey(queryKey), [queryKey])

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryHash === queryHash) {
          onStoreChange()
        }
      }),
    [queryClient, queryHash]
  )

  const getSnapshot = useCallback(
    () => queryClient.getQueryData<T>(queryKey),
    [queryClient, queryKey]
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
